import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAuthSession } from "@/authSessionClient";
import PhoneInput from "react-phone-number-input";
import type { E164Number } from "libphonenumber-js";
import "react-phone-number-input/style.css";

type DeliveryChannel = "phone" | "email";

const havingTroubleMailto = `mailto:kosh@kennedyryon.com?subject=${encodeURIComponent("Can't Access the Album")}&body=${encodeURIComponent(
  `Hey team, 

I'm having trouble logging in. 

Help!
`,
)}`;

export function LoginPage() {
  const navigate = useNavigate();
  const [channel, setChannel] = useState<DeliveryChannel>("phone");
  const [phone, setPhone] = useState<E164Number | undefined>(undefined);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAuthSession()
      .then((r) => r.json())
      .then((d: { authenticated?: boolean }) => {
        if (d.authenticated) navigate("/", { replace: true });
      })
      .catch(() => {});
  }, [navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (channel === "phone") {
      if (!phone?.trim()) {
        setError("Phone number is required");
        return;
      }
    } else if (!email.trim()) {
      setError("Email address is required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/auth/request-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(
          channel === "phone"
            ? { channel: "phone" as const, phoneNumber: phone }
            : { channel: "email" as const, email: email.trim() },
        ),
      });
      const data = (await res.json()) as { error?: string; sessionId?: string };
      if (!res.ok) {
        setError(data.error ?? "Something went wrong");
        return;
      }
      if (!data.sessionId) {
        setError("Missing session");
        return;
      }
      navigate("/verify", {
        state: { sessionId: data.sessionId, delivery: channel },
        replace: true,
      });
    } catch {
      setError("Network error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen text-white flex flex-col bg-black">
      <div className="flex shrink-0 justify-center px-6 pt-[max(1.5rem,env(safe-area-inset-top))] pb-6">
        <img
          src="/assets/kennedy-logo.webp"
          alt="Kennedy Ryon"
          className="max-w-[120px] h-auto"
        />
      </div>
      <div className="flex flex-1 flex-col items-center px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center">
            <img
              src="/assets/evolve.jpeg"
              alt="Can I Evolve? album art"
              className="w-40 mx-auto mb-4 rounded-lg shadow-xl"
            />
            <h1 className="text-2xl font-bold">Can I Evolve? Digital Album</h1>
            <p className="text-gray-400 text-sm mt-2">
              Enter the phone number or email you used to purchase to access the album
            </p>
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <span className="block text-sm text-white mb-2">Sign in with</span>
              <div
                className="flex rounded-lg border border-white/20 p-0.5 bg-white/5"
                role="group"
                aria-label="Phone or email"
              >
                <button
                  type="button"
                  onClick={() => {
                    setChannel("phone");
                    setError(null);
                  }}
                  className={
                    "flex-1 rounded-md py-2 text-sm font-medium transition-colors text-white " +
                    (channel === "phone" ? "bg-white/15 shadow-sm" : "opacity-90 hover:opacity-100")
                  }
                >
                  Phone
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setChannel("email");
                    setError(null);
                  }}
                  className={
                    "flex-1 rounded-md py-2 text-sm font-medium transition-colors text-white " +
                    (channel === "email" ? "bg-white/15 shadow-sm" : "opacity-90 hover:opacity-100")
                  }
                >
                  Email
                </button>
              </div>
            </div>
            {channel === "phone" ? (
              <div>
                <label htmlFor="phone" className="block text-sm text-white mb-1">
                  Phone number
                </label>
                <PhoneInput
                  defaultCountry="US"
                  countrySelectProps={{ unicodeFlags: true }}
                  value={phone}
                  onChange={setPhone}
                  limitMaxLength
                  className="PhoneInput w-full rounded-lg bg-white/10 border border-white/20 px-2 py-1.5 text-white [--PhoneInput-color--focus:var(--primary)] [--PhoneInputCountryFlag-borderColor:rgba(255,255,255,0.35)] focus-within:ring-2 focus-within:ring-primary [&_.PhoneInputInput]:min-h-9 [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:text-white [&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:border-0 [&_.PhoneInputInput]:placeholder:text-gray-500"
                  numberInputProps={{
                    id: "phone",
                    autoComplete: "tel",
                  }}
                />
              </div>
            ) : (
              <div>
                <label htmlFor="email" className="block text-sm text-white mb-1">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-lg bg-white/10 border border-white/20 px-3 py-2.5 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            )}
            {error && <p className="text-sm text-red-400">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className={
                "w-full mt-1.5 inline-flex items-center justify-center gap-2 rounded-[10px] border-0 px-4 py-[15px] " +
                "bg-[#9EBB89] text-base font-bold text-black " +
                "transition-[transform,box-shadow,background-color,opacity] duration-[400ms] ease-in-out " +
                "hover:-translate-y-0.5 hover:bg-[#C7D9B7] hover:shadow-[0_10px_36px_rgba(199,217,183,0.25)] " +
                "disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:bg-[#9EBB89] disabled:hover:shadow-none"
              }
            >
              {submitting ? "Sending…" : "Get Access"}
            </button>
            <div className="text-center">
              <a
                href={havingTroubleMailto}
                className="text-sm text-gray-400 underline underline-offset-2 hover:text-white inline-block"
              >
                Having trouble?
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
